//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { createTheme } from '@mui/material'
import type {} from '@mui/x-data-grid-premium/themeAugmentation'

declare module '@mui/material/styles' {
  // interface Theme {
  //
  // }
  //
  interface TypeBackground {
    muted?: string
    subtle?: string
    canvas?: string
  }

  interface TypeText {
    muted?: string
    subtle?: string
    accent?: string
    onDisabled?: string
  }

  interface Palette {
    accent1: Palette['primary']
    accent2: Palette['primary']
    accent3: Palette['primary']
    fg: Palette['primary']
    apy: Palette['primary']
  }

  interface PaletteOptions {
    accent1: PaletteOptions['primary']
    accent2: PaletteOptions['primary']
    accent3: PaletteOptions['primary']
    fg: PaletteOptions['primary']
    apy: PaletteOptions['primary']
  }
  interface TypographyVariants {
    lead?: TypographyVariants['h1']
    leadBold?: TypographyVariants['h1']
    small?: TypographyVariants['body1']
    smallBold?: TypographyVariants['body1']
    xsmall?: TypographyVariants['body1']
    tiny?: TypographyVariants['body1']
    body?: TypographyVariants['body1']
    bodyMuted?: TypographyVariants['body1']
    bodyBold?: TypographyVariants['body1']
    h1Bold?: TypographyVariants['h2']
    h2Bold?: TypographyVariants['h2']
    h3Bold?: TypographyVariants['h2']
    h4Bold?: TypographyVariants['h2']
    h5Bold?: TypographyVariants['h2']
    h6Bold?: TypographyVariants['h2']
    captionBold?: TypographyVariants['body1']
    captionMuted?: TypographyVariants['body1']
    buttonBold?: TypographyVariants['body1']
  }
  interface TypographyVariantsOptions {
    lead?: TypographyVariantsOptions['h1']
    leadBold?: TypographyVariantsOptions['h1']
    small?: TypographyVariantsOptions['body1']
    smallBold?: TypographyVariantsOptions['body1']
    xsmall?: TypographyVariantsOptions['body1']
    tiny?: TypographyVariantsOptions['body1']
    body?: TypographyVariantsOptions['body1']
    bodyMuted?: TypographyVariantsOptions['body1']
    bodyBold?: TypographyVariantsOptions['body1']
    h1Bold?: TypographyVariantsOptions['h2']
    h2Bold?: TypographyVariantsOptions['h2']
    h3Bold?: TypographyVariantsOptions['h2']
    h4Bold?: TypographyVariantsOptions['h2']
    h5Bold?: TypographyVariantsOptions['h2']
    h6Bold?: TypographyVariantsOptions['h2']
    captionBold?: TypographyVariantsOptions['body1']
    captionMuted?: TypographyVariantsOptions['body1']
    buttonBold?: TypographyVariantsOptions['body1']
  }
  interface PaletteColor {
    muted?: string
    subtle?: string
    emphasis?: string
    fg?: string
  }

  interface SimplePaletteColorOptions {
    muted?: string
    subtle?: string
    emphasis?: string
    disabled?: string
    fg?: string
    onWarning?: string
    onError?: string
    onInfo?: string
    onSuccess?: string
  }

  // interface ThemeOptions {
  //    status: {
  //      danger: React.CSSProperties['color']
  //    }
  // }
}

export const theme = createTheme({
  spacing: 4,
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h2',
          h2: 'h2',
          h3: 'h2',
          h4: 'h2',
          h5: 'h2',
          h6: 'h2',
          subtitle1: 'h2',
          subtitle2: 'h2',
          body: 'h2',
          body1: 'span',
          body2: 'h2',
          h1Bold: 'h2',
          h2Bold: 'h2',
          h3Bold: 'h2',
          h4Bold: 'h2',
          h5Bold: 'h2',
          h6Bold: 'h2',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#424242',
          borderColor: '#E0E0E0',
          margin: '0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#616161',
          fontSize: '14px',
          '&.Mui-selected': {
            fontWeight: 700,
            color: '#171717',
            borderRadius: '25px',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        cell: {
          lineHeight: '1',
          alignContent: 'center',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: '#E0E0E0',
          color: '#171717',
          backgroundColor: '#F5F5F5',
          '&:hover': {
            borderColor: '#E0E0E0',
            color: '#171717',
            backgroundColor: '#EEEEEE',
          },
          '&:focus': {
            borderColor: '#E0E0E0',
            color: '#171717',
            backgroundColor: '#F5F5F5',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          // Black fill instead of primary blue
          '&.Mui-checked': {
            color: '#000000',
            '& .MuiSvgIcon-root': {
              // More padding for the checkmark icon
              padding: '2px',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          // Indeterminate state (for "Select All" checkboxes)
          '&.MuiCheckbox-indeterminate': {
            color: '#000000',
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 3,
  },
  typography: {
    fontFamily: ['Plus Jakarta Sans', 'Roboto', 'Open Sans'].join(','),
    h1: {
      fontSize: '37px',
      fontWeight: 500,
      lineHeight: '40.7px',
      fontFamily: 'Plus Jakarta Sans',
    },
    h1Bold: {
      fontSize: '37px',
      fontWeight: 700,
      lineHeight: '40.7px',
      fontFamily: 'Plus Jakarta Sans',
    },
    h2: {
      fontSize: '32px',
      fontWeight: 500,
      lineHeight: '35.2px',
      fontFamily: 'Plus Jakarta Sans',
    },
    h2Bold: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: '35.2px',
      fontFamily: 'Plus Jakarta Sans',
    },
    h3: {
      fontSize: '28px',
      lineHeight: '30.8px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    h3Bold: {
      fontSize: '28px',
      lineHeight: '30.8px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
    },
    h4: {
      fontSize: '24px',
      lineHeight: '31.2px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    h4Bold: {
      fontSize: '24px',
      lineHeight: '31.2px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
    },
    h5: {
      fontSize: '18px',
      lineHeight: '23.4px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    h5Bold: {
      fontSize: '18px',
      lineHeight: '23.4px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
    },
    h6: {
      fontSize: '16px',
      lineHeight: '20.8px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    h6Bold: {
      fontSize: '16px',
      lineHeight: '20.8px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
    },
    lead: {
      fontSize: '21px',
      lineHeight: '31.5px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
      textTransform: 'capitalize',
    },
    leadBold: {
      fontSize: '21px',
      lineHeight: '31.5px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
      textTransform: 'capitalize',
    },
    body1: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    body2: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
    },
    bodyBold: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
      textTransform: 'none',
    },
    body: {
      fontSize: '16px',
      textAlign: 'left',
      lineHeight: '24px',
      textSizeAdjust: '100%',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    bodyMuted: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
      color: '#616161',
    },
    small: {
      fontSize: '14px',
      lineHeight: '21px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    smallBold: {
      fontSize: '14px',
      lineHeight: '21px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
    },
    xsmall: {
      fontSize: '10px',
      lineHeight: '15px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    tiny: {
      fontSize: '9px',
      lineHeight: '13.5px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },

    subtitle1: {
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    subtitle2: {
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
      color: '#616161',
    },
    caption: {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    captionMuted: {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
      color: '#616161',
    },
    captionBold: {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
    },
    button: {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 500,
      fontFamily: 'Plus Jakarta Sans',
    },
    buttonBold: {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 700,
      fontFamily: 'Plus Jakarta Sans',
      textTransform: 'none',
    },
  },
  palette: {
    fg: {
      main: '#171717',
      fg: '#FFFFFF',
      muted: '#616161',
    },
    background: {
      default: '#FCFCFC',
      paper: '#FCFCFC',
      muted: '#E0E0E0',
      subtle: '#EEEEEE',
      canvas: '#F5F5F5',
    },
    accent1: {
      main: '#66B8EA',
      emphasis: '#0088DC',
      muted: '#99CFF1',
      subtle: '#E6F3FC',
      disabled: '#E0E0E0',
    },
    accent2: {
      main: '#41D677',
      emphasis: '#11CC55',
      muted: '#70E099',
      subtle: '#E7FAEE',
      disabled: '#E0E0E0',
    },
    accent3: {
      main: '#F6D663',
      emphasis: '#F4CC3C',
      muted: '#F8E08A',
      subtle: '#FEFAEC',
      disabled: '#FEFAEC',
    },

    warning: {
      main: '#B38327',
      fg: '#B38327',
      emphasis: '#B38327',
      muted: '#FFE4AF',
      subtle: '#FFE4AF',
      onWarning: '#171717',
    },
    error: {
      main: '#9C0F2E',
      fg: '#9C0F2E',
      emphasis: '#DF1642',
      muted: '#E95C7B',
      subtle: '#FCE8EC',
      onError: '#FFFFFF',
    },
    info: {
      main: '#3142C4',
      fg: '#3142C4',
      emphasis: '#3D53F5',
      muted: '#8B98F9',
      subtle: '#D8DDFD',
      onInfo: '#FFFFFF',
    },
    success: {
      main: '#11763D',
      fg: '#11763D',
      emphasis: '#18A957',
      muted: '#5DC389',
      subtle: '#E8F6EE',
      onSuccess: '#FFFFFF',
    },
    primary: {
      main: '#66B8EA',
    },
    secondary: {
      main: '#E769A6', // pink
    },
    apy: {
      main: '#4CAF50',
    },

    text: {
      primary: '#171717',
      secondary: '#000000',
      muted: '#616161',
      subtle: '#9E9E9E',
      accent: '#171717',
      disabled: '#757575',
      onDisabled: '#BDBDBD',
    },
  },
})

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    small: true
    xsmall: true
    smallBold: true
    lead: true
    leadBold: true
    body: true
    bodyMuted: true
    bodyBold: true
    h1Bold: true
    h2Bold: true
    h3Bold: true
    h4Bold: true
    h5Bold: true
    h6Bold: true
    captionMuted: true
    captionBold: true
    buttonBold: true
  }
}
